import { portalFetch } from "@/lib/portalApi";
import type {
  B2CClientConnector,
  B2CClientConnectorProperty,
  B2CConnectorType,
  PortalOrganisation,
} from "@/types/portal";

export async function fetchOrganisations(
  token: string,
): Promise<PortalOrganisation[]> {
  // Fetch only the user's organisations (not all orgs in the system)
  const response = await portalFetch<{
    items: Array<{ id: string; name: string; slug: string; role: string }>;
  }>("/users/me/organisations", {
    method: "GET",
    token,
  });

  // Transform from the /users/me/organisations format to PortalOrganisation
  const organisations: PortalOrganisation[] = (response?.items ?? []).map(
    (org) => ({
      organisationId: org.id,
      organisationName: org.name,
      role: org.role as PortalOrganisation["role"],
      data: {
        Id: org.id,
        CompanyName: org.name,
      },
    }),
  );

  return organisations;
}

export async function fetchConnectorTypes(
  token: string,
  organisationId: string,
): Promise<B2CConnectorType[]> {
  if (!organisationId) return [];
  const types = await portalFetch<B2CConnectorType[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types/org/${organisationId}`,
    {
      method: "GET",
      token,
    },
  );
  return types ?? [];
}

export async function fetchAllConnectorTypes(
  token: string,
): Promise<B2CConnectorType[]> {
  const types = await portalFetch<B2CConnectorType[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types`,
    {
      method: "GET",
      token,
    },
  );
  return types ?? [];
}

export async function fetchClientConnectors(
  token: string,
  organisationId: string,
  connectorType: string,
): Promise<B2CClientConnector[]> {
  if (!organisationId || !connectorType) return [];
  try {
    // Fetch all connectors for org, then filter by type client-side
    const connectors = await portalFetch<B2CClientConnector[]>(
      `/api/v1/gateway/business2cloud/api/v1/connectors/org/${organisationId}`,
      {
        method: "GET",
        token,
      },
    );
    // Filter by SourceConnectorType (field from B2C API, JOINed from B2CConnector.Type)
    return (connectors ?? []).filter(
      (c) => c.SourceConnectorType === connectorType,
    );
  } catch (error) {
    // Some connector types may not exist for an organisation – fail silently
    return [];
  }
}

export async function fetchConnectorProperties(
  token: string,
  _organisationId: string,
  connectorId: number | string,
): Promise<B2CClientConnectorProperty[]> {
  const props = await portalFetch<B2CClientConnectorProperty[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/properties`,
    {
      method: "GET",
      token,
    },
  );
  return props ?? [];
}

export async function updateConnectorProperties(
  token: string,
  _organisationId: string,
  connectorId: number | string,
  properties: B2CClientConnectorProperty[],
): Promise<void> {
  // Data-plane expects { properties: { key: value } } format
  const propsObject: Record<string, string> = {};
  for (const prop of properties) {
    propsObject[prop.Name] = prop.Value;
  }
  await portalFetch(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/properties`,
    {
      method: "PATCH",
      token,
      body: { properties: propsObject },
    },
  );
}

export async function updateConnector(
  token: string,
  _organisationId: string,
  connectorId: number | string,
  updates: { name?: string; schedule?: string },
): Promise<void> {
  await portalFetch(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}`,
    {
      method: "PATCH",
      token,
      body: updates,
    },
  );
}

/**
 * Delete connector properties by name.
 * Used when changing target types to remove old target-specific properties.
 * Note: Data-plane doesn't have a delete properties endpoint yet - setting to empty string instead.
 */
export async function deleteConnectorProperties(
  token: string,
  _organisationId: string,
  connectorId: number | string,
  propertyNames: string[],
): Promise<void> {
  // Set properties to empty string to "delete" them
  const propsObject: Record<string, string> = {};
  for (const name of propertyNames) {
    propsObject[name] = "";
  }
  await portalFetch(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/properties`,
    {
      method: "PATCH",
      token,
      body: { properties: propsObject },
    },
  );
}

/**
 * Fetch available target connector types for SQL Server source.
 * Filters to IsSelfService = true and Status = 'ACTIVE'.
 */
export async function fetchTargetConnectorTypes(
  token: string,
): Promise<B2CConnectorType[]> {
  const allTypes = await fetchAllConnectorTypes(token);

  // Filter to self-service, active targets that can receive data from SQL Server
  // Currently supported: SqlServer, Xero, FTP
  const supportedTargets = ["SqlServer", "Xero", "FTP"];

  return allTypes.filter(
    (t) => t.Status === "ACTIVE" && supportedTargets.includes(t.Type),
  );
}

export type AuthorizedConnection = {
  connectorId: number;
  connectorName: string;
  properties: Record<string, string>;
};

/**
 * Fetch authorized connections for a specific connector type.
 * These are existing connectors that have been authorized (e.g., Xero OAuth completed).
 */
export async function fetchAuthorizedConnections(
  token: string,
  organisationId: string,
  connectorType: string,
): Promise<AuthorizedConnection[]> {
  if (!organisationId || !connectorType) return [];

  try {
    // Fetch all connectors of this type for the organisation
    const connectors = await fetchClientConnectors(
      token,
      organisationId,
      connectorType,
    );

    // Filter to active connectors that have authorization properties
    const authorizedConnectors: AuthorizedConnection[] = [];

    for (const connector of connectors) {
      if (connector.Status !== "ACTIVE") continue;

      // Fetch properties for this connector
      const properties = await fetchConnectorProperties(
        token,
        organisationId,
        connector.Id,
      );

      // Check if it has authorization properties (e.g., xero.tenantId for Xero)
      const propsMap: Record<string, string> = {};
      for (const prop of properties) {
        propsMap[prop.Name] = prop.Value;
      }

      // For Xero, check for tenantId
      if (connectorType === "Xero" && propsMap["xero.tenantId"]) {
        authorizedConnectors.push({
          connectorId: connector.Id,
          connectorName: connector.Name,
          properties: propsMap,
        });
      }
      // Add other connector types as needed
    }

    return authorizedConnectors;
  } catch (error) {
    console.error("Error fetching authorized connections:", error);
    return [];
  }
}

export type CreateConnectorRequest = {
  name: string;
  sourceConnectorType: string;
  targetConnectorId: number;
  referenceConnectorId?: number;
  properties: Record<string, string>;
};

/**
 * Create a new client connector.
 */
export async function createClientConnector(
  token: string,
  organisationId: string,
  request: CreateConnectorRequest,
): Promise<B2CClientConnector> {
  // Data-plane expects: { name, sourceConnectorType, schedule? }
  const connector = await portalFetch<B2CClientConnector>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/org/${organisationId}`,
    {
      method: "POST",
      token,
      body: {
        name: request.name,
        sourceConnectorType: request.sourceConnectorType,
        schedule: null,
      },
    },
  );
  return connector;
}

// ============================================================================
// Connector Type Admin API Functions (Platform Admin)
// ============================================================================

export type AdminConnectorType = B2CConnectorType & {
  IsSelfService?: boolean;
  IsEarlyStages?: boolean;
  IsInsider?: boolean;
  BatchSize?: number;
  BufferSize?: number;
  // Per-TYPE default table-name prefix (e.g. "X" for Xero, "C7" for Cin7).
  // Generic sources (MySql, SqlServer) leave this null so customer
  // connectors supply a custom value via the Discover UI.
  TargetTablePrefix?: string | null;
  AccentColour?: string;
  DisplayName?: string;
  Description?: string;
  IconUrl?: string;
};

export type ConnectorTypeUpdateRequest = {
  // Operational fields (written to b2c.B2CConnector)
  IsSelfService?: boolean;
  IsEarlyStages?: boolean;
  IsInsider?: boolean;
  Status?: string;
  BatchSize?: number;
  BufferSize?: number;
  TargetTablePrefix?: string | null;
  // Visual fields (written to dp_business2cloud.ConnectorTypeMetadata)
  AccentColour?: string;
  DisplayName?: string;
  Description?: string;
  IconUrl?: string;
};

export type AdminConnectorTypesResponse = {
  items: AdminConnectorType[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    page: number;
    pages: number;
  };
};

export async function fetchAdminConnectorTypes(
  token: string,
  params?: { limit?: number; offset?: number; search?: string; sort_by?: string; sort_order?: "asc" | "desc" },
): Promise<AdminConnectorTypesResponse> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));
  if (params?.search) qs.set("search", params.search);
  if (params?.sort_by) qs.set("sort_by", params.sort_by);
  if (params?.sort_order) qs.set("sort_order", params.sort_order);
  const queryString = qs.toString();
  const result = await portalFetch<AdminConnectorTypesResponse>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types/admin${queryString ? `?${queryString}` : ""}`,
    {
      method: "GET",
      token,
    },
  );
  return result ?? { items: [], pagination: { limit: 25, offset: 0, total: 0, page: 1, pages: 0 } };
}

export async function updateConnectorType(
  token: string,
  connectorTypeId: number | string,
  data: ConnectorTypeUpdateRequest,
): Promise<AdminConnectorType> {
  const result = await portalFetch<AdminConnectorType>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types/${connectorTypeId}`,
    {
      method: "PUT",
      token,
      body: data,
    },
  );
  if (!result) {
    throw new Error("Failed to update connector type");
  }
  return result;
}

/**
 * Create a brand-new connector type (admin only).
 *
 * Type must match a registered C# ISourceConnector.Type or syncs will
 * fail at runtime — back-end doesn't (and can't without runtime type
 * introspection against the C# engine) validate this, so the admin is
 * expected to coordinate with the engine team before adding a row.
 */
/**
 * B2C licence model drill-down — read-only view of what each model gates.
 *
 * A licence model in B2C is a capability gate the C# refresh engine consults
 * at sync time: rows in `B2CLicenceModelClient` decide which
 * `B2CConnectorRecord` templates get synced for a given customer. CP's
 * `ServiceFeature.B2CLicenceModelId` points at these rows for entitlement
 * pricing but doesn't write them — that's still manual. This drill-down is
 * the operator's window into the gating layer.
 */
export type LicenceModelRecord = {
  Id: number;
  Name: string;
};

export type LicenceModelConnector = {
  ConnectorId: number;
  Type: string;
  IsDefault: boolean;
  Records: LicenceModelRecord[];
};

export type LicenceModel = {
  Id: number;
  Description: string | null;
  Base: boolean;
  ClientCount: number;
  Connectors: LicenceModelConnector[];
};

export async function fetchLicenceModels(
  token: string,
): Promise<LicenceModel[]> {
  const result = await portalFetch<LicenceModel[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/licence-models/admin`,
    { method: "GET", token },
  );
  return result ?? [];
}

/**
 * Per-instance "advanced" sub-table bindings for a single client connector.
 * Read-only view backing the Advanced accordion on ConnectorPropertiesSheet.
 *
 * Connection property values are NEVER returned (the DP endpoint strips them
 * to avoid leaking tokens). The portal renders the property NAME only and
 * shows a small note explaining values are not exposed.
 */
export type ClientConnectorFlow = {
  Id: number;
  RecordId: number | null;
  TargetRecordId: number | null;
  RecordName: string | null;
  TargetRecordName: string | null;
  RecordProperty: string | null;
  Precedence: number | null;
  ProcessSequentially: boolean | null;
  Disabled: boolean | null;
  Detail: string | null;
  TargetClientConnectorId: number | null;
  Filters: ClientConnectorFlowFilter[];
};

export type ClientConnectorFlowFilter = {
  Id: number;
  ClientConnectorFlowId: number;
  Property: string | null;
  Value: string | null;
  Operator: string | null;
};

export type ClientConnectorRecordInterceptorBinding = {
  Id: number;
  ClientConnectorRecordId: number;
  RecordName: string | null;
  ExecutionStep: string | null;
  ExecutionOrder: number | null;
};

export type ClientConnectorConnection = {
  Id: number;
  Description: string | null;
  SourceConnectorId: number | null;
  TargetConnectorId: number | null;
  SourceConnectorType: string | null;
  TargetConnectorType: string | null;
  Properties: { Id: number; Name: string }[];
};

export type ConnectorAdvancedBindings = {
  flows: ClientConnectorFlow[];
  recordInterceptors: ClientConnectorRecordInterceptorBinding[];
  resultInterceptors: ClientConnectorRecordInterceptorBinding[];
  writeInterceptors: ClientConnectorRecordInterceptorBinding[];
  connections: ClientConnectorConnection[];
};

export async function fetchConnectorAdvancedBindings(
  token: string,
  connectorId: number | string,
): Promise<ConnectorAdvancedBindings> {
  const result = await portalFetch<ConnectorAdvancedBindings>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/advanced`,
    { method: "GET", token },
  );
  return (
    result ?? {
      flows: [],
      recordInterceptors: [],
      resultInterceptors: [],
      writeInterceptors: [],
      connections: [],
    }
  );
}

// ---------------------------------------------------------------------------
// Connector field mappings (read) + clone
// ---------------------------------------------------------------------------

export type ConnectorRecordPropertyMapping = {
  id: number;
  name: string | null;
  source: string | null;
  order: number | null;
  type: string | null;
  primary: boolean;
  nullable: boolean;
  disabled: boolean;
  default_value: string | null;
  interpolated_value: string | null;
  computed_value: string | null;
  // Column typing — returned by the read API; carried through the editor so a
  // declarative-replace PUT round-trips SQL-target column definitions faithfully.
  length: number | null;
  precision: number | null;
  scale: number | null;
};

export type ConnectorRecordMapping = {
  id: number;
  name: string | null;
  source: string | null;
  url_path: string | null;
  identifier: string | null;
  parent_id: number | null;
  disabled: boolean;
  /** FTP sources: the file-name regex this record picks up. Optional so the
   *  portal tolerates a data plane that predates the field. */
  source_name_structure?: string | null;
  /** FTP sources: the sheet (`Sheet/<name>` / `Table/<name>`); null = first sheet. */
  object_path?: string | null;
  properties: ConnectorRecordPropertyMapping[];
};

export type ConnectorRecordMappings = {
  connector_id: number;
  records: ConnectorRecordMapping[];
};

/** Full record + field-mapping detail for a connector (read-only). */
export async function fetchConnectorMappings(
  token: string,
  connectorId: number | string,
): Promise<ConnectorRecordMappings> {
  const result = await portalFetch<ConnectorRecordMappings>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/mappings`,
    { method: "GET", token },
  );
  // portalFetch returns {} for an empty-body 200 — guard on `records` so callers
  // never hit `.length` on undefined.
  return result?.records
    ? result
    : { connector_id: Number(connectorId), records: [] };
}

// ---------------------------------------------------------------------------
// Connector field mappings (write) — declarative full-set replace per record
// ---------------------------------------------------------------------------

/** One property in a declarative replace. Same shape the read API returns, so
 *  GET → edit → PUT round-trips (order is taken from array position). */
export type MappingWriteInput = {
  name: string;
  source: string | null;
  type: string | null;
  primary: boolean;
  nullable: boolean;
  length: number | null;
  precision: number | null;
  scale: number | null;
  disabled: boolean;
  default_value: string | null;
  interpolated_value: string | null;
  computed_value: string | null;
};

export type MappingIssue = {
  severity: string; // "error" | "warning" | …
  message: string;
  property_name?: string | null;
};

/** Result of a dry-run validate OR a real replace: structural issues + the
 *  INSERT/UPDATE/DELETE diff the change would apply. */
export type MappingWriteResult = {
  record_name: string;
  dry_run: boolean;
  issues: MappingIssue[];
  inserted: number;
  updated: number;
  deleted: number;
};

function mappingsPath(connectorId: number | string, recordName: string, suffix: string): string {
  return `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/${encodeURIComponent(
    recordName,
  )}/${suffix}`;
}

// The validate/replace responses gate a DESTRUCTIVE full-replace, so both fail
// CLOSED: a missing/mismatched body (portalFetch yields {} on an empty 200, or
// the contract drifts) must NOT be synthesised into a clean pass / silent
// success — that would let an unvalidated or unknown-outcome write through.
function assertWriteResult(
  result: MappingWriteResult | undefined,
  what: string,
): MappingWriteResult {
  if (!result?.record_name) {
    throw new Error(`${what} returned no result — not saving. Reload and retry.`);
  }
  return result;
}

/** Dry-run validate a candidate mapping set: structural issues + diff counts,
 *  no write. Platform-admin only (same gate as the write). Fails closed. */
export async function validateRecordMappings(
  token: string,
  connectorId: number | string,
  recordName: string,
  properties: MappingWriteInput[],
): Promise<MappingWriteResult> {
  const result = await portalFetch<MappingWriteResult>(
    mappingsPath(connectorId, recordName, "validate"),
    { method: "POST", token, body: { properties } },
  );
  return assertWriteResult(result, "Validate");
}

/** Declarative full-set replace of a record's field mappings (anything absent
 *  is deleted). Platform-admin only; every change is audited server-side.
 *  Fails closed on a missing response. */
export async function replaceRecordMappings(
  token: string,
  connectorId: number | string,
  recordName: string,
  properties: MappingWriteInput[],
): Promise<MappingWriteResult> {
  const result = await portalFetch<MappingWriteResult>(
    mappingsPath(connectorId, recordName, "mappings"),
    { method: "PUT", token, body: { properties } },
  );
  return assertWriteResult(result, "Save");
}

// ---------------------------------------------------------------------------
// Client connector record settings / delete / table changes (FTP setup, D13/D19/D21)
// ---------------------------------------------------------------------------

function recordPath(connectorId: number | string, recordId: number, suffix = ""): string {
  return `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/${recordId}${suffix}`;
}

/** Partial update of a record's settings. Only the keys present are applied;
 *  `null` clears a nullable setting. A rename needs `confirm_rename: true`. */
export type RecordSettingsUpdate = {
  name?: string;
  confirm_rename?: boolean;
  identifier?: string;
  disabled?: boolean;
  source_name_structure?: string | null;
  object_path?: string | null;
  source?: string | null;
  header_row?: number | null;
  delimiter?: string | null;
  /** Data rows dropped after the headings (`ftp.source.skipRows.{id}`). */
  skip_rows?: number | null;
};

/** The settings as the data plane returns them after an update. Every field is
 *  optional: the UI merges whatever comes back over what it sent. */
export type RecordSettings = {
  id?: number;
  name?: string | null;
  identifier?: string | null;
  disabled?: boolean;
  source_name_structure?: string | null;
  object_path?: string | null;
  source?: string | null;
  header_row?: number | null;
  delimiter?: string | null;
  skip_rows?: number | null;
};

/** PATCH a client connector record's settings. Platform-admin only. */
export async function updateRecordSettings(
  token: string,
  connectorId: number | string,
  recordId: number,
  body: RecordSettingsUpdate,
): Promise<RecordSettings> {
  const result = await portalFetch<RecordSettings>(recordPath(connectorId, recordId, "/settings"), {
    method: "PATCH",
    token,
    body,
  });
  return result ?? {};
}

/** Delete a client connector record (its mappings, filters, configuration and
 *  per-record FTP properties). The target table and its data are kept. */
export async function deleteConnectorRecord(
  token: string,
  connectorId: number | string,
  recordId: number,
): Promise<void> {
  await portalFetch(recordPath(connectorId, recordId), { method: "DELETE", token });
}

export type TableChangeKind = "add" | "add_computed" | "widen" | "blocked" | "unmapped";

export type TableChangeItem = {
  column: string;
  kind: TableChangeKind | string;
  /** The exact SQL that would run; null for blocked / unmapped items. */
  statement: string | null;
  reason?: string | null;
  /** The column as it is in the table now (null when it doesn't exist). */
  current_definition?: string | null;
  /** The column as the mappings define it (null for an unmapped column). */
  mapped_definition?: string | null;
};

export type TableChanges = {
  table_exists: boolean;
  schema?: string | null;
  table?: string | null;
  items: TableChangeItem[];
};

/** Kinds a person can tick and apply. Everything else is information only. */
export const APPLICABLE_TABLE_CHANGE_KINDS: ReadonlySet<string> = new Set([
  "add",
  "add_computed",
  "widen",
]);

function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/** How a record's mappings differ from its target table. Read-only. Field
 *  names are read defensively (`column`/`name`, `statement`/`sql`) so a small
 *  contract drift renders rather than blanks. */
export async function getTableChanges(
  token: string,
  connectorId: number | string,
  recordId: number,
): Promise<TableChanges> {
  const raw = await portalFetch<Record<string, unknown>>(
    recordPath(connectorId, recordId, "/table-changes"),
    { method: "GET", token },
  );
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.changes)
      ? raw.changes
      : [];
  const items: TableChangeItem[] = (list as Record<string, unknown>[])
    .map((it) => ({
      column: str(it?.column) ?? str(it?.name) ?? str(it?.column_name) ?? "",
      kind: str(it?.kind) ?? "unknown",
      statement: str(it?.statement) ?? str(it?.sql),
      reason: str(it?.reason),
      current_definition: str(it?.current_definition),
      mapped_definition: str(it?.mapped_definition),
    }))
    .filter((it) => it.column !== "");
  const tableExists = raw?.table_exists !== false;
  return {
    table_exists: tableExists,
    schema: str(raw?.schema),
    table: str(raw?.table),
    // A table that doesn't exist yet is created in full by the next refresh.
    items: tableExists ? items : [],
  };
}

export type ApplyTableChangesResult = {
  /** The statements that ran. */
  executed: string[];
  /** Columns someone else applied concurrently: information, not an error. */
  already_applied: string[];
};

/** Apply the ticked table changes. The server recomputes the diff and refuses
 *  (409) if any requested column is no longer applicable. */
export async function applyTableChanges(
  token: string,
  connectorId: number | string,
  recordId: number,
  columns: string[],
): Promise<ApplyTableChangesResult> {
  const raw = await portalFetch<Record<string, unknown>>(
    recordPath(connectorId, recordId, "/table-changes/apply"),
    { method: "POST", token, body: { apply: columns } },
  );
  const list = Array.isArray(raw?.executed)
    ? raw.executed
    : Array.isArray(raw?.statements)
      ? raw.statements
      : [];
  // Each executed entry is {column, kind, statement}; a bare string is tolerated.
  const executed = (list as unknown[])
    .map((e) =>
      typeof e === "string"
        ? e
        : e && typeof e === "object"
          ? str((e as Record<string, unknown>).statement)
          : null,
    )
    .filter((s): s is string => !!s);
  const already = Array.isArray(raw?.already_applied) ? raw.already_applied : [];
  return {
    executed,
    already_applied: (already as unknown[]).filter((c): c is string => typeof c === "string"),
  };
}

/** Sample the FTP and propose records. `headerRow` (0 = no headings, name
 *  columns by position) and `skipRows` (data rows dropped after the headings)
 *  are only sent when given, so the connector's own defaults apply otherwise. */
export async function discoverFtpFiles<T>(
  token: string | undefined,
  connectorId: number | string,
  headerRow?: number | null,
  skipRows?: number | null,
): Promise<T> {
  const params = new URLSearchParams();
  if (headerRow !== undefined && headerRow !== null) params.set("header_row", String(headerRow));
  if (skipRows !== undefined && skipRows !== null) params.set("skip_rows", String(skipRows));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return portalFetch<T>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/discover-files${qs}`,
    { method: "POST", token },
  );
}

export type ConnectorSourceColumns = {
  connector_id: number;
  status: "ok" | "not_a_sql_source" | "source_unreachable" | "source_schema_empty";
  source_schema: string | null;
  reason?: string;
  columns: Record<string, string[]>; // source object name → sorted column names
};

/** Live source columns per object for a connector — powers the editor's source
 *  dropdown / {{Fluid}} autocomplete so an admin picks a REAL column. Opens a
 *  short-lived introspection connection to the source, so it's on-demand only. */
export async function fetchConnectorSourceColumns(
  token: string,
  connectorId: number | string,
): Promise<ConnectorSourceColumns> {
  const result = await portalFetch<ConnectorSourceColumns>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/source-columns`,
    { method: "GET", token },
  );
  if (!result?.status) {
    return {
      connector_id: Number(connectorId),
      status: "source_unreachable",
      source_schema: null,
      columns: {},
    };
  }
  return { ...result, columns: result.columns ?? {} };
}

export type RecordTargetFields = {
  connector_id: number;
  record_name: string;
  /** ok = a catalog to offer · no_catalog = the target defines no field set
   *  (SqlServer, where the mappings themselves create the columns) ·
   *  unavailable = the lookup failed. These are three DIFFERENT things: `ok`
   *  with an empty list would render an authoritative-looking empty dropdown. */
  status: "ok" | "no_catalog" | "unavailable";
  reason: string | null;
  target_type: string | null;
  advisory: boolean;
  fields: string[]; // sorted target-field paths
};

/** Target-field catalog for one record — powers the editor's target-field
 *  picker (WS1 P2 slice 5c), the counterpart of fetchConnectorSourceColumns.
 *
 *  ADVISORY. Absence from `fields` does NOT mean a mapping is invalid: scratch
 *  fields consumed downstream are legitimate and the catalog is only as current
 *  as the engine's pinned SDK. Never surface a "did you mean" correction for a
 *  field that isn't listed — renaming a dead field to the nearest valid name is
 *  how a boolean source column would have overwritten live customer emails. */
export async function fetchRecordTargetFields(
  token: string,
  connectorId: number | string,
  recordName: string,
): Promise<RecordTargetFields> {
  const result = await portalFetch<RecordTargetFields>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/${encodeURIComponent(recordName)}/target-fields`,
    { method: "GET", token },
  );
  if (!result?.status) {
    return {
      connector_id: Number(connectorId),
      record_name: recordName,
      status: "unavailable",
      reason: "no response",
      target_type: null,
      advisory: true,
      fields: [],
    };
  }
  // Defensive: only offer a list when the backend said `ok`. Any other status
  // must leave the input as free text.
  return {
    ...result,
    fields: result.status === "ok" ? (result.fields ?? []) : [],
  };
}

/** One field's rendered output for one sample row — what the connector WOULD
 *  send at write time (tier-3 semantic preview). */
export type MappingPreviewField = {
  name: string | null;
  source: string | null;
  interpolated_value: string | null;
  rendered: string | null; // null = a genuine SQL/JSON null
  is_null: boolean;
  error: string | null; // per-field template parse / render error
  non_deterministic: boolean; // uses {% uuid %} — value changes every run
};

export type MappingPreviewRow = { fields: MappingPreviewField[] };

/** Where the sample rows came from (best-effort — the preview still runs with
 *  zero rows so template PARSE errors are surfaced). */
export type MappingPreviewSample = {
  status:
    | "ok"
    | "no_source_object"
    | "not_a_sql_source"
    | "source_unreachable"
    | "source_object_not_found"
    | string;
  source_object: string | null;
  rows_fetched: number;
  reason: string | null;
};

export type MappingPreviewResult = {
  rows: MappingPreviewRow[];
  template_errors: { name: string | null; error: string | null }[];
  applied_rules: string; // "SqlServer" | "Passthrough"
  rows_rendered: number;
  rows_supplied: number;
  rows_skipped: number;
  truncated: boolean;
  sample: MappingPreviewSample;
};

/** Semantic preview: render a record's DRAFT mappings through the write-time
 *  transform against a few live sample rows, returning the per-field
 *  sent-vs-expected diff. Read-only; platform-admin only. Throws a clear error
 *  if the engine is unreachable / the response shape is absent. */
export async function fetchMappingPreview(
  token: string,
  connectorId: number | string,
  recordName: string,
  properties: MappingWriteInput[],
  sampleLimit = 5,
): Promise<MappingPreviewResult> {
  const result = await portalFetch<MappingPreviewResult>(
    mappingsPath(connectorId, recordName, "preview"),
    { method: "POST", token, body: { properties, sample_limit: sampleLimit } },
  );
  if (!result?.applied_rules) {
    throw new Error(
      "Preview returned no result — the transform engine may be unavailable. Try again.",
    );
  }
  return {
    ...result,
    rows: result.rows ?? [],
    template_errors: result.template_errors ?? [],
    sample: result.sample ?? {
      status: "unknown",
      source_object: null,
      rows_fetched: 0,
      reason: null,
    },
  };
}

// ---- Mapping templates (WS1 P3) ---------------------------------------------

export type MappingTemplateBinding = {
  slot: string;
  label: string;
  kind: "column" | "literal" | string;
  required: boolean;
  help_text: string;
};

export type MappingTemplate = {
  key: string;
  version: number;
  target_type: string;
  record_name: string;
  description: string;
  identifier: string | null;
  bindings: MappingTemplateBinding[];
};

/** The versioned field-mapping templates the editor can stamp onto a record —
 *  each declares the per-client bindings it needs. Platform-admin. The registry
 *  is connector-agnostic (a global endpoint), so no connector id is needed. */
export async function fetchMappingTemplates(
  token: string,
): Promise<MappingTemplate[]> {
  const result = await portalFetch<{ templates: MappingTemplate[] }>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/mapping-templates`,
    { method: "GET", token },
  );
  return (result?.templates ?? []).map((t) => ({ ...t, bindings: t.bindings ?? [] }));
}

/** The registry lists EVERY (key, version) — keep only the highest version of
 *  each key, so a picker never offers two indistinguishable entries for one
 *  template. Computed from `version`, not the response order. Keys keep the
 *  position of their first appearance. */
export function latestMappingTemplates(templates: MappingTemplate[]): MappingTemplate[] {
  const byKey = new Map<string, MappingTemplate>();
  for (const t of templates) {
    const current = byKey.get(t.key);
    if (!current || t.version > current.version) byKey.set(t.key, t);
  }
  return [...byKey.values()];
}

export type ApplyTemplateResult = MappingWriteResult & {
  template_key: string;
  template_version: number;
  properties_applied: number;
};

/** Stamp a template onto a record: expand + declaratively replace its mappings.
 *  `dryRun` validates + diffs without writing. Returns the write result (issues +
 *  INSERT/UPDATE/DELETE counts) + the template applied. Fails closed. */
export async function applyRecordTemplate(
  token: string,
  connectorId: number | string,
  recordName: string,
  args: {
    templateKey: string;
    version: number;
    bindings: Record<string, string>;
    dryRun?: boolean;
    // Connector-create path (PR4): create the record from the template first if
    // it doesn't exist yet. `recordSource` is the source view/table it reads.
    createIfAbsent?: boolean;
    recordSource?: string;
  },
): Promise<ApplyTemplateResult> {
  const result = await portalFetch<ApplyTemplateResult>(
    mappingsPath(connectorId, recordName, "apply-template"),
    {
      method: "POST",
      token,
      body: {
        template_key: args.templateKey,
        version: args.version,
        bindings: args.bindings,
        dry_run: args.dryRun ?? false,
        create_if_absent: args.createIfAbsent ?? false,
        record_source: args.recordSource,
      },
    },
  );
  if (!result?.record_name) {
    throw new Error("Apply-template returned no result — reload and retry.");
  }
  return result;
}

// ---- Template drift (WS1 P3) ------------------------------------------------

export type TemplateDriftFieldChange = {
  name: string;
  fields: Record<string, { expected: unknown; current: unknown }>;
};

export type TemplateDrift = {
  connector_id: number;
  record_name: string;
  status:
    | "ok"
    | "no_template"
    | "template_error"
    | "record_missing"
    | "record_ambiguous"
    | string;
  template_key: string | null;
  template_version: number | null;
  bindings: Record<string, string>;
  in_sync: boolean | null;
  reason?: string;
  drift: { added: string[]; removed: string[]; changed: TemplateDriftFieldChange[] };
};

/** How a record's live mappings have drifted from the template it was stamped
 *  from (added / removed / changed). Read-only; platform-admin, org-scoped. */
export async function fetchRecordTemplateDrift(
  token: string,
  connectorId: number | string,
  recordName: string,
): Promise<TemplateDrift> {
  const result = await portalFetch<TemplateDrift>(
    mappingsPath(connectorId, recordName, "template-drift"),
    { method: "GET", token },
  );
  return {
    ...result,
    drift: result?.drift ?? { added: [], removed: [], changed: [] },
    bindings: result?.bindings ?? {},
  };
}

export type MappingDoctorViolation = {
  connector_id: number;
  record_name: string | null;
  ref_kind: "record_source" | "property_source" | "token";
  reference: string;
  source_object: string | null;
  reason: string;
  property_name: string | null;
};

export type MappingDoctorReport = {
  connector_id: number;
  status: "ok" | "not_a_sql_source" | "source_schema_empty" | "source_unreachable";
  reason?: string;
  source_schema?: string;
  checked_records: number;
  violation_count: number;
  violations: MappingDoctorViolation[];
};

/**
 * Referential health check for a connector's mappings (friction #8): resolves
 * every record Source + property {{token}} against the LIVE source schema and
 * reports the dangling references. Read-only, on-demand.
 */
export async function fetchConnectorMappingDoctor(
  token: string,
  connectorId: number | string,
): Promise<MappingDoctorReport> {
  const result = await portalFetch<MappingDoctorReport>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/mappings/doctor`,
    { method: "GET", token },
  );
  // portalFetch returns {} for an empty-body 200 — guard so callers can rely on
  // the shape.
  if (!result?.status) {
    return {
      connector_id: Number(connectorId),
      status: "source_unreachable",
      reason: "no response from the doctor",
      checked_records: 0,
      violation_count: 0,
      violations: [],
    };
  }
  return { ...result, violations: result.violations ?? [] };
}

export type MappingDoctorFleetResult = {
  connector_id: number;
  organisation_id: string | null;
  /**
   * Connector + owning-client names, resolved server-side at read time (the
   * cache row itself only stores the id). Null when the connector no longer
   * exists — the card then falls back to "Connector #<id>" and is not
   * clickable, since the All Instances deep-link matches on NAME.
   */
  connector_name: string | null;
  organisation_name: string | null;
  status: "ok" | "not_a_sql_source" | "source_schema_empty" | "source_unreachable";
  violation_count: number;
  checked_records: number;
  source_schema: string | null;
  reason: string | null;
  duration_ms: number | null;
  checked_at: string | null;
};

/**
 * The last fleet sweep's cached mapping-doctor results (one per connector).
 * Feeds the platform "Mapping health" card. Read-only; platform-admin.
 */
export async function fetchMappingDoctorResults(
  token: string,
): Promise<MappingDoctorFleetResult[]> {
  const result = await portalFetch<{ results?: MappingDoctorFleetResult[] }>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/mapping-doctor/results`,
    { method: "GET", token },
  );
  return result?.results ?? [];
}

/**
 * Enqueue a fleet mapping-doctor sweep (refreshes the cache in the background).
 * Returns the job id; platform-admin only.
 */
export async function triggerMappingDoctorSweep(
  token: string,
): Promise<{ status: string; job_id: string }> {
  return portalFetch(
    `/api/v1/gateway/business2cloud/api/v1/connectors/mapping-doctor/sweep`,
    { method: "POST", token },
  );
}

export type CloneMappingsResult = {
  records_cloned: number;
  properties_cloned: number;
  record_names: string[];
};

/** Clone record + field-mapping rows from another connector onto this one. */
export async function cloneConnectorMappings(
  token: string,
  connectorId: number | string,
  sourceConnectorId: number,
  options?: { recordNames?: string[]; allowCrossOrg?: boolean },
): Promise<CloneMappingsResult> {
  const body: Record<string, unknown> = {
    allowCrossOrg: options?.allowCrossOrg ?? false,
  };
  if (options?.recordNames && options.recordNames.length > 0) {
    body.recordNames = options.recordNames;
  }
  return portalFetch<CloneMappingsResult>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/records/clone-from/${sourceConnectorId}`,
    { method: "POST", token, body },
  );
}

export type OrgConnectorSummary = {
  Id: number;
  Name: string;
  SourceConnectorType?: string;
  SourceConnectorId?: number;
  TargetConnectorId?: number;
  Status?: string;
};

/** All connectors for an org — used to pick a clone SOURCE. */
export async function listOrgConnectors(
  token: string,
  organisationId: string,
): Promise<OrgConnectorSummary[]> {
  const result = await portalFetch<OrgConnectorSummary[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/org/${organisationId}`,
    { method: "GET", token },
  );
  return Array.isArray(result) ? result : [];
}

/**
 * A single template record (B2CConnectorRecord) attached to a connector type.
 * Platform admins read-only view today; write operations are a follow-up PR.
 */
export type ConnectorTemplateRecord = {
  Id: number;
  ConnectorId: number;
  ParentId: number | null;
  Name: string;
  Source: string | null;
  Identifier: string | null;
  Offset: string | null;
  Since: string | null;
  Sort: string | null;
  BatchSize: number | null;
  Paged: boolean | null;
  IsExternal: boolean | null;
  AlwaysDrop: boolean | null;
  Disabled: boolean | null;
  ProcessSequentially: boolean | null;
  DeleteChildren: boolean | null;
  ApiPath: string | null;
  UrlPath: string | null;
  ObjectPath: string | null;
  Properties: ConnectorTemplateRecordProperty[];
};

export type ConnectorTemplateRecordProperty = {
  Id: number;
  ConnectorRecordId: number;
  Name: string;
  Source: string | null;
  Order: number | null;
  Type: string | null;
  Primary: boolean | null;
  Nullable: boolean | null;
  Length: number | null;
  Precision: number | null;
  Scale: number | null;
  Disabled: boolean | null;
  DefaultValue: string | null;
  InterpolatedValue: string | null;
  ComputedValue: string | null;
};

/**
 * Fetch the template records (B2CConnectorRecord + nested properties) for a
 * connector type. Generic source types (SqlServer, MySql, FTP) return an
 * empty list by design — their records live per-client in
 * B2CClientConnectorRecord instead of in the template catalogue.
 */
export async function fetchConnectorTypeRecords(
  token: string,
  connectorTypeId: number | string,
): Promise<ConnectorTemplateRecord[]> {
  const result = await portalFetch<ConnectorTemplateRecord[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types/admin/${connectorTypeId}/records`,
    { method: "GET", token },
  );
  return result ?? [];
}

/**
 * Shape returned by the DP proxy of the C# engine's
 * ``/api/source-connectors/types`` endpoint. The ``type`` is the value
 * the admin "Add connector type" dialog writes into
 * ``b2c.B2CConnector.Type``; ``className`` is for diagnostics only.
 */
export type RegisteredSourceConnectorType = {
  type: string;
  className: string;
};

/**
 * Fetch the authoritative list of source connector types registered in
 * the running C# engine. Drives the admin "Add connector type"
 * dropdown so the list can't drift out of sync with the engine.
 */
export async function fetchRegisteredSourceConnectorTypes(
  token: string,
): Promise<RegisteredSourceConnectorType[]> {
  const result = await portalFetch<RegisteredSourceConnectorType[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/source-types/registered`,
    { method: "GET", token },
  );
  return result ?? [];
}

export type ConnectorTypeCreateRequest = {
  Type: string;
  BatchSize?: number;
  BufferSize?: number;
  IsSelfService?: boolean;
  RequiresAuthorisation?: boolean;
  IsEarlyStages?: boolean;
  IsInsider?: boolean;
  Status?: string;
  TargetTablePrefix?: string | null;
  AccentColour?: string;
  DisplayName?: string;
  Description?: string;
  IconUrl?: string;
};

export async function createConnectorType(
  token: string,
  data: ConnectorTypeCreateRequest,
): Promise<AdminConnectorType> {
  const result = await portalFetch<AdminConnectorType>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/types/admin`,
    {
      method: "POST",
      token,
      body: data,
    },
  );
  if (!result) {
    throw new Error("Failed to create connector type");
  }
  return result;
}

export type AdminConnectorInstance = {
  Id: number;
  Name: string;
  Key: string;
  Status: string;
  Schedule?: string;
  ConnectorType: string;
  OrgKey: string;
  OrgName: string;
  DeletedAtUtc?: string;
  /**
   * The CONNECTION this row's leg belongs to — supplied by the server, not
   * derived here.
   *
   * A row is a leg, not a connector: Haesloop 480 (Xero>SQL) and 484
   * (SQL>Xero) are one Xero organisation. This list is cross-org and
   * paginated, so two legs can arrive on different pages; grouping in the
   * browser would silently group only whatever shared a page. Sort by
   * `connection` to bring them together.
   *
   * Null when the leg has no external system (SqlServer at both ends — never
   * an integration) or its identity is unavailable.
   */
  ConnectionKey?: string | null;
  ConnectionLabel?: string | null;
  ExternalSystem?: string | null;
  Direction?: "inbound" | "outbound" | "internal" | null;
  TargetConnectorType?: string | null;
};

export type AdminConnectorInstancesResponse = {
  items: AdminConnectorInstance[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    page: number;
    pages: number;
  };
};

export async function fetchAdminConnectorInstances(
  token: string,
  filters?: {
    status?: string;
    connectorType?: string;
    orgId?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  },
): Promise<AdminConnectorInstancesResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  // Server's FastAPI Query is aliased to camelCase ("connectorType"
  // and "orgId"), so the client must send those exact names. The
  // earlier snake_case versions were silently ignored — type filter
  // appeared to work but the server returned every instance.
  if (filters?.connectorType) params.set("connectorType", filters.connectorType);
  if (filters?.orgId) params.set("orgId", filters.orgId);
  if (filters?.search) params.set("search", filters.search);
  if (filters?.limit) params.set("limit", String(filters.limit));
  if (filters?.offset !== undefined) params.set("offset", String(filters.offset));
  if (filters?.sort_by) params.set("sort_by", filters.sort_by);
  if (filters?.sort_order) params.set("sort_order", filters.sort_order);
  const qs = params.toString();
  const result = await portalFetch<AdminConnectorInstancesResponse>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/instances/admin${qs ? `?${qs}` : ""}`,
    {
      method: "GET",
      token,
    },
  );
  return result ?? { items: [], pagination: { limit: 25, offset: 0, total: 0, page: 1, pages: 0 } };
}

// ============================================================================
// Promote client connector → connector type
// ============================================================================
//
// See lara-data-plane/src/business2cloud/docs/promotion-path.md for the
// architecture. Promote lifts a client connector's per-client records
// (created by the SqlServer "Discover + Seed" flow) into platform-level
// B2CConnectorRecord templates under a new B2CConnector row, sets
// `BaseTransport` so the engine's alias mechanism resolves the new Type
// to an existing class with no new C# code required.
//
// Platform admin only. Defaults to dry-run; the portal flow always
// dry-runs first to show the operator the diff before applying.

export type PromoteToTypeRequest = {
  type: string;
  displayName?: string;
  dryRun?: boolean;
  deleteClientRecords?: boolean;
};

export type PromoteToTypeResponse = {
  newConnectorId: number | null;
  type: string;
  recordsToInsert: number;
  propertiesToInsert: number;
  clientConnectorRepoint: {
    clientConnectorId: number;
    fromSourceConnectorId: number | null;
    toSourceConnectorId: number | null;
  };
  perClientRowsToDelete: number;
  preCheck: {
    csharpTypeRegistered: boolean;
    typeAlreadyExists: boolean;
    encryptedPropertiesPreserved: boolean;
    resolvedBaseTransport: string | null;
  };
  recordIdMap: Record<string, number>;
};

export async function promoteConnectorInstance(
  token: string,
  connectorId: number,
  request: PromoteToTypeRequest,
): Promise<PromoteToTypeResponse> {
  const result = await portalFetch<PromoteToTypeResponse>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/promote-to-type`,
    { method: "POST", token, body: request },
  );
  if (!result) {
    throw new Error("Promote-to-type returned no body");
  }
  return result;
}

/**
 * Hot-reload the engine's connector alias map. Called automatically
 * after a successful promote-apply so the new alias resolves on the
 * next refresh — without this the engine wouldn't pick the new alias
 * up until the next restart.
 *
 * Returns `{ reloaded: true }` on success, or a diagnostic object if
 * the engine is unreachable or returned non-2xx (the portal surfaces
 * this as a warning, not a hard failure — the promote itself
 * succeeded; reload is a best-effort follow-up).
 */
export async function reloadConnectorAliasMap(
  token: string,
): Promise<{ reloaded: boolean; engineMessage?: string }> {
  const result = await portalFetch<{ reloaded: boolean; engineMessage?: string }>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/source-types/reload-aliases`,
    { method: "POST", token },
  );
  return result ?? { reloaded: false };
}

// ============================================================================
// Connector Subscription API Functions (per-connector-type billing)
// ============================================================================

export type ConnectorSubscription = {
  id: string;
  organisation_id: string;
  connector_type_id: string;
  service_tier_id: string;
  status: "ACTIVE" | "CANCELLED" | "TRIAL";
  billing_interval: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  tier_name: string | null;
  tier_display_name: string | null;
  price_monthly_zar: number | null;
  created_at: string;
  updated_at: string;
};

export type ConnectorSubscriptionListResponse = {
  organisation_id: string;
  subscriptions: ConnectorSubscription[];
  total_monthly_zar: number;
};

export type ConnectorAccessCheckResponse = {
  has_access: boolean;
  organisation_id: string;
  connector_type_id: string;
  tier: string | null;
  status: string | null;
};

export async function fetchConnectorSubscriptions(
  token: string,
  organisationId: string,
): Promise<ConnectorSubscriptionListResponse> {
  return portalFetch<ConnectorSubscriptionListResponse>(
    `/api/v1/connector-subscriptions/organisation/${organisationId}`,
    { method: "GET", token },
  );
}

export async function createConnectorSubscription(
  token: string,
  organisationId: string,
  data: { connector_type_id: string; tier: string; billing_interval?: string },
): Promise<ConnectorSubscription> {
  return portalFetch<ConnectorSubscription>(
    `/api/v1/connector-subscriptions/organisation/${organisationId}`,
    {
      method: "POST",
      token,
      body: JSON.stringify({
        organisation_id: organisationId,
        ...data,
      }),
    },
  );
}

export async function changeConnectorTier(
  token: string,
  organisationId: string,
  connectorTypeId: string,
  tier: string,
): Promise<ConnectorSubscription> {
  return portalFetch<ConnectorSubscription>(
    `/api/v1/connector-subscriptions/organisation/${organisationId}/${connectorTypeId}/tier`,
    {
      method: "PUT",
      token,
      body: JSON.stringify({ tier }),
    },
  );
}

export async function cancelConnectorSubscription(
  token: string,
  organisationId: string,
  connectorTypeId: string,
  immediately: boolean = false,
): Promise<ConnectorSubscription> {
  return portalFetch<ConnectorSubscription>(
    `/api/v1/connector-subscriptions/organisation/${organisationId}/${connectorTypeId}?immediately=${immediately}`,
    { method: "DELETE", token },
  );
}

// Schema name validation regex: starts with letter, alphanumeric + underscore only
const SCHEMA_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

// Reserved schema names that cannot be used
const RESERVED_SCHEMAS = new Set([
  "dbo",
  "guest",
  "sys",
  "b2c",
  "information_schema",
  "INFORMATION_SCHEMA",
]);

// ============================================================================
// Global Configuration
// ============================================================================

export type B2CGlobalConfigItem = {
  Id: string;
  Key: string;
  Value: string;
  Description: string;
  CreatedAtUtc: string | null;
  UpdatedAtUtc: string | null;
};

export type RunningConnector = {
  Id: number;
  Name: string;
  Status: string;
  ConnectorType: string;
  OrgName: string;
  OrgKey: string;
  TriggeredAtUtc: string;
  TriggerType: "manual" | "automatic";
};

export async function fetchGlobalConfig(
  token: string,
): Promise<B2CGlobalConfigItem[]> {
  const items = await portalFetch<B2CGlobalConfigItem[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/global-config`,
    { method: "GET", token },
  );
  return items ?? [];
}

export async function updateGlobalConfig(
  token: string,
  key: string,
  value: string,
): Promise<B2CGlobalConfigItem> {
  return portalFetch<B2CGlobalConfigItem>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/global-config/${encodeURIComponent(key)}`,
    { method: "PUT", token, body: { value } },
  );
}

export async function fetchRunningConnectors(
  token: string,
): Promise<RunningConnector[]> {
  const items = await portalFetch<RunningConnector[]>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/running`,
    { method: "GET", token },
  );
  return items ?? [];
}

export type ConnectorRunItem = {
  connector_id: number;
  connector_name: string | null;
  error: string | null;
  minutes_stale: number | null;
};

export type ConnectorRunHealth = {
  connected: boolean;
  failing_count: number;
  overdue_count: number;
  failing: ConnectorRunItem[];
  overdue: ConnectorRunItem[];
  checked_at: string | null;
};

/**
 * Connector RUN-outcome health: active connectors whose latest run errored
 * (failing) or scheduled connectors that stopped completing (overdue). Same
 * signal the 4-hourly platform-health digest carries, exposed on-request.
 * Owned by the Control Plane platform-health authority (not the b2c gateway).
 */
export async function fetchConnectorRunHealth(
  token: string,
): Promise<ConnectorRunHealth> {
  return portalFetch<ConnectorRunHealth>(
    `/api/v1/platform/connectors/run-health`,
    { method: "GET", token },
  );
}

/**
 * One (workflow, trigger) pair's run summary.
 *
 * Keyed on the TRIGGER, not just the workflow: a workflow can carry several, and a
 * healthy one would otherwise mask a dead sibling. That is not hypothetical — it is
 * how the 2026-07-24 Haesloop outage stayed hidden for four days.
 */
export type WorkflowRunItem = {
  /** WorkflowKey is unique only per organisation, so two orgs may share one. */
  organisation_id: string | null;
  /** Set on STUCK items only: one item per suspended execution, so this is what
   *  makes the item unique — several runs of one trigger can pile up behind an
   *  approval node. */
  execution_id: string | null;
  workflow_key: string;
  workflow_name: string | null;
  trigger_id: string;
  error: string | null;
  consecutive_failures: number | null;
  failing_since: string | null;
  minutes_stale: number | null;
  suspended_node_id: string | null;
  minutes_overdue: number | null;
};

/**
 * One recurring failure pattern from the workflows failure sweep (Loop A): a trigger
 * that failed the same way `failure_count` times in the sweep's 7-day window. Keyed on
 * (organisation, trigger, error signature) — never the definition, which is null for
 * inline-DSL runs. `workflow_key` / `workflow_name` are null when the newest failing
 * execution ran an inline definition.
 */
export type WorkflowFailureFindingItem = {
  organisation_id: string;
  trigger_id: string;
  error_signature: string;
  workflow_key: string | null;
  workflow_name: string | null;
  failure_count: number;
  first_seen: string;
  last_seen: string;
  /** The most frequent verbatim error message in the cluster. */
  modal_error: string;
  sample_execution_id: string;
};

export type WorkflowRunHealth = {
  connected: boolean;
  failing_count: number;
  stalled_count: number;
  stuck_count: number;
  /** Optional: absent from a Control Plane older than the with-errors state. */
  with_errors_count?: number;
  failing: WorkflowRunItem[];
  stalled: WorkflowRunItem[];
  stuck: WorkflowRunItem[];
  /** Triggers whose newest run reached End but took a failure exit on the way
   *  (`completed_with_errors`); `error` names the node and exit. */
  with_errors?: WorkflowRunItem[];
  checked_at: string | null;
  // Loop A recurring-failure findings. Optional because a Control Plane older than the
  // findings collector omits them — which the card must read as "unreachable", never
  // as "no recurring failures". Read independently of `connected`.
  findings_connected?: boolean;
  finding_count?: number;
  findings?: WorkflowFailureFindingItem[];
  findings_sweep_ran?: boolean;
  findings_sweep_completed_at?: string | null;
  minutes_since_findings_sweep?: number | null;
};

/**
 * v2 workflow RUN-outcome health: triggers whose runs are failing, scheduled
 * subscriptions that have stopped running, and suspended executions past their own
 * deadline. Same signal the 4-hourly platform-health digest carries, exposed
 * on-request. Owned by the Control Plane platform-health authority.
 */
export async function fetchWorkflowRunHealth(
  token: string,
): Promise<WorkflowRunHealth> {
  return portalFetch<WorkflowRunHealth>(
    `/api/v1/platform/workflows/run-health`,
    { method: "GET", token },
  );
}

/** One customer VPN peer's liveness summary. */
export type WireguardPeerItem = {
  peer_id: string;
  name: string;
  organisation_name: string | null;
  allowed_ips: string | null;
  /** Minutes since the last handshake. `null` means the peer has NEVER
   *  handshaked — provisioned but the client site never brought the tunnel up.
   *  A distinct failure from "was up, went down", so it must not be rendered
   *  as a number (0 minutes ago would read as perfectly healthy). */
  minutes_since_handshake: number | null;
  minutes_since_created: number | null;
};

export type WireguardHealth = {
  connected: boolean;
  offline_count: number;
  stale_count: number;
  healthy_count: number;
  provisioning_count: number;
  offline: WireguardPeerItem[];
  stale: WireguardPeerItem[];
  provisioning: WireguardPeerItem[];
  checked_at: string | null;
};

/**
 * CUSTOMER VPN tunnel health — client-site gateways only.
 *
 * Deliberately not derived from `getVpnStatus`, which returns the raw WireGuard
 * interface dump: its `active_peers` counts EVERY peer, including internal team
 * laptops that are offline by design most of the day. Reading health off that
 * makes the cards show an outage every evening, and buries a genuinely down
 * client site among shut laptops.
 *
 * Scope (`PeerType='customer'` AND `Status='active'`), the <2m / 2-10m / >=10m
 * bands, and the provisioning grace window all live in the Control Plane
 * collector — the same one the 4-hourly digest reads, so the portal and the
 * Teams alert can never disagree. Do NOT re-derive any of it here: that rule
 * already has four hand-copied implementations that have drifted apart, and
 * this must not become the fifth.
 */
export async function fetchWireguardHealth(
  token: string,
): Promise<WireguardHealth> {
  return portalFetch<WireguardHealth>(`/api/v1/platform/wireguard/health`, {
    method: "GET",
    token,
  });
}

/** One scheduled task's latest-run summary. */
export type ScheduleRunItem = {
  schedule_id: string;
  organisation_id: string | null;
  service_name: string;
  task_name: string;
  display_name: string | null;
  cron: string | null;
  error: string | null;
  minutes_since_last_run: number | null;
};

export type ScheduleRunHealth = {
  connected: boolean;
  failing_count: number;
  overdue_count: number;
  /** Optional: absent from a Control Plane older than the with-errors state. */
  with_errors_count?: number;
  failing: ScheduleRunItem[];
  overdue: ScheduleRunItem[];
  /** Recent fires that finished but could not process every target. */
  with_errors?: ScheduleRunItem[];
  checked_at: string | null;
};

/**
 * Control Plane scheduler run health: tasks that failed (including ones the ticker
 * auto-disabled because their cron stopped parsing) and ones that have stopped
 * firing. Same signal the 4-hourly platform-health digest carries, on-request.
 */
export async function fetchScheduleRunHealth(
  token: string,
): Promise<ScheduleRunHealth> {
  return portalFetch<ScheduleRunHealth>(
    `/api/v1/platform/schedules/run-health`,
    { method: "GET", token },
  );
}

// ============================================================================
// Schema Validation
// ============================================================================

/**
 * Validate schema name format on the client side.
 * Returns null if valid, or an error message if invalid.
 */
export function validateSchemaNameFormat(schemaName: string): string | null {
  if (!schemaName || !schemaName.trim()) {
    return "Schema name is required";
  }

  const trimmed = schemaName.trim();

  if (trimmed.length > 128) {
    return "Schema name must be 128 characters or less";
  }

  if (!SCHEMA_NAME_REGEX.test(trimmed)) {
    return "Schema name must start with a letter and contain only letters, numbers, underscores, and hyphens";
  }

  if (
    RESERVED_SCHEMAS.has(trimmed) ||
    RESERVED_SCHEMAS.has(trimmed.toLowerCase())
  ) {
    return `'${trimmed}' is a reserved schema name and cannot be used`;
  }

  return null;
}

// ─── Provider specs (connector templating W4) ────────────────────────────────
// A connector TYPE's template registration (connector row, record tree, per-record
// properties / interceptors / filters / configuration, licence links) as one
// versioned JSON document in blob. Writing the spec changes NOTHING in b2c; the
// apply route is what executes it, through the same audited record-admin actions
// the interactive editor uses, under one revertable ApplyRunId.
//
// Platform-admin only, and the writes additionally require an INTERACTIVE admin —
// a service identity can read the drift routes but never write.

const SPEC_BASE = "/api/v1/gateway/business2cloud/api/v1/connectors/types/admin";

/**
 * A provider spec, typed structurally.
 *
 * Deliberately NOT a field-by-field mirror of the pydantic model (7 nested models,
 * ~46 fields): the editor round-trips the document as JSON and the server re-validates
 * it with `extra="forbid"`, so a hand-mirrored interface would drift silently while
 * looking authoritative. Only what the UI itself reads is typed.
 */
export type ProviderSpec = {
  spec_version: number;
  type: string;
  connector?: Record<string, unknown>;
  records?: Array<{ name: string; [k: string]: unknown }>;
  licence_bundle?: Array<{
    id: number;
    description: string;
    base?: boolean;
    default_on_create?: boolean;
  }>;
  baselined_from?: Record<string, unknown> | null;
  /** Live anomalies the baseline recorded verbatim — it never normalises live state. */
  warnings?: string[];
  [k: string]: unknown;
};

export type ProviderSpecResponse = {
  type: string;
  connector_type_id: number;
  /** Null when the blob container is unversioned (dev fake) — treated as "no expectation". */
  version_id: string | null;
  spec: ProviderSpec;
};

export type ProviderSpecVersion = {
  version_id: string;
  is_current: boolean;
  size: number;
  last_modified: string | null;
};

export type ProviderSpecVersionsResponse = {
  type: string;
  connector_type_id: number;
  versions: ProviderSpecVersion[];
};

/** One change an apply would execute, in execution order. */
export type ProviderSpecChange = {
  kind: string;
  record?: string;
  /** record.create only: the parent record's name (null = top-level). */
  parent?: string | null;
  /** record.update / connector.update: field → {before, after}. */
  fields?: Record<string, { before?: unknown; after?: unknown }>;
  before?: unknown;
  after?: unknown;
};

export type ProviderSpecDrift = {
  type: string;
  connector_type_id: number;
  in_sync: boolean;
  change_count: number;
  counts: Record<string, number>;
  changes: ProviderSpecChange[];
  warnings: string[];
  /** Present on the per-type drift route: the spec version the diff was taken at. */
  spec_version_id?: string | null;
};

export type ProviderSpecApplyResult = {
  dry_run: boolean;
  /** Null on a dry run; the revert handle after a real apply — including a partial one. */
  apply_run_id: string | null;
  type: string;
  connector_type_id: number;
  spec_version_id: string | null;
  enable: boolean;
  planned: ProviderSpecDrift;
  /** Non-empty → a real apply would be REFUSED (400). Not warnings. */
  preflight: Array<{ record: string | null; error: string }>;
  applied: Array<{ index: number; kind: string; record: string | null }>;
  failed: Record<string, unknown> | null;
  enabled: string[];
  /** D17: what stayed disabled because `enable` was false. */
  enable_withheld: Array<{ record: string | null; reason: string }>;
  complete: boolean;
  /** Real applies only: the drift that remains after the run. */
  remaining?: ProviderSpecDrift;
  /** dp #2392: planned changes that would destroy something in use — a delete, or a
   *  property / licence-link removal, on a record with load history or while the
   *  type has an ACTIVE client connector. Each runs only when confirmed. Optional
   *  (`?? []`): absent on a backend that predates the gate. */
  destructive?: ProviderSpecDestructive[];
  /** What the run did not execute because it was not confirmed (plus the ancestor
   *  deletes a skipped child delete blocks). On a dry run: what WOULD be skipped. */
  skipped_destructive?: ProviderSpecSkipped[];
  confirm_delete?: string[];
  confirm_remove?: ProviderSpecConfirmRemove[];
  /** b2c-spec-autocatchup: the root records this apply marks for a catch-up (every
   *  connector of the type re-pulls them at its next run). A dry run carries
   *  `planned: true` and no timestamp; `error` = the apply landed but the marks did
   *  not. Optional: absent on a backend that predates catch-up. */
  catch_up?: ProviderSpecCatchUpOutcome;
};

export type ProviderSpecCatchUpOutcome = {
  records: string[];
  requested_at_utc?: string | null;
  planned?: boolean;
  error?: string;
};

export type ProviderSpecDestructiveKind =
  | "record.delete"
  /** dp #2392 D25: a record.update that disables a loading record. */
  | "record.update"
  | "record.replace_properties"
  | "record.replace_licence_models";

export type ProviderSpecDestructive = {
  kind: ProviderSpecDestructiveKind;
  record: string;
  /** record.delete only: the live parent (null = top-level). */
  parent?: string | null;
  /** Replace kinds only: property names / licence model ids the change removes. */
  removed?: Array<string | number>;
  record_log_rows: number;
  rows_loaded: number;
  last_loaded_at_utc: string | null;
  active_client_connectors: number;
  destructive: boolean;
};

export type ProviderSpecSkipped = {
  kind: ProviderSpecDestructiveKind;
  record: string;
  reason: string;
};

export type ProviderSpecConfirmRemove = {
  record: string;
  kind: Exclude<ProviderSpecDestructiveKind, "record.delete">;
};

export type ProviderSpecBaselineResult = {
  type: string;
  connector_type_id: number;
  version_id: string;
  previous_version_id?: string | null;
  records: number;
  warnings: string[];
  spec?: ProviderSpec;
};

export type ProviderSpecBaselineAllResult = {
  baselined: number;
  types: Array<{
    type: string;
    connector_type_id: number;
    version_id: string;
    records: number;
    warnings: string[];
  }>;
  /** Types that could not be represented (a ParentId cycle, a duplicate name). */
  errors: Array<{ type: string; connector_type_id: number; error: string }>;
};

export type RevertRunResult = {
  apply_run_id: string;
  revert_run_id: string;
  total: number;
  /** dp returns the reverted change sets themselves (record_admin.revert_run). */
  reverted: Array<{ change_set_id: string; sequence: number; action: string; record: string | null }>;
  skipped_already_reverted: unknown[];
  failed: Record<string, unknown> | null;
  audit_gaps: number | unknown[];
  run_unterminated: boolean;
  complete: boolean;
  note: string | null;
};

export async function fetchConnectorTypeSpec(
  token: string,
  connectorTypeId: number | string,
): Promise<ProviderSpecResponse> {
  return portalFetch<ProviderSpecResponse>(
    `${SPEC_BASE}/${connectorTypeId}/spec`,
    { method: "GET", token },
  );
}

export async function fetchConnectorTypeSpecVersions(
  token: string,
  connectorTypeId: number | string,
): Promise<ProviderSpecVersionsResponse> {
  return portalFetch<ProviderSpecVersionsResponse>(
    `${SPEC_BASE}/${connectorTypeId}/spec/versions`,
    { method: "GET", token },
  );
}

/**
 * BLOB WRITE ONLY — changes nothing in b2c until an apply runs.
 *
 * `expectedVersionId` is the version the editor loaded (null = "no spec exists yet");
 * a mismatch is refused with 409 and the current id, so the editor reloads rather
 * than overwriting an edit made elsewhere.
 */
export async function putConnectorTypeSpec(
  token: string,
  connectorTypeId: number | string,
  spec: ProviderSpec,
  expectedVersionId: string | null,
): Promise<{ type: string; connector_type_id: number; version_id: string }> {
  return portalFetch(`${SPEC_BASE}/${connectorTypeId}/spec`, {
    method: "PUT",
    token,
    body: { spec, expected_version_id: expectedVersionId },
  });
}

/** BLOB WRITE: record live state as the spec. 409 unless `force` when one exists. */
export async function baselineConnectorTypeSpec(
  token: string,
  connectorTypeId: number | string,
  force = false,
): Promise<ProviderSpecBaselineResult> {
  return portalFetch<ProviderSpecBaselineResult>(
    `${SPEC_BASE}/${connectorTypeId}/spec/baseline${force ? "?force=true" : ""}`,
    { method: "POST", token },
  );
}

/** BLOB WRITE, first-run only: 409 if ANY spec already exists. */
export async function baselineAllConnectorTypeSpecs(
  token: string,
): Promise<ProviderSpecBaselineAllResult> {
  return portalFetch<ProviderSpecBaselineAllResult>(
    `${SPEC_BASE}/spec/baseline-all`,
    { method: "POST", token },
  );
}

/** Read-only, LIVE (not the swept verdict): the ordered change list an apply would run. */
export async function fetchConnectorTypeSpecDrift(
  token: string,
  connectorTypeId: number | string,
): Promise<ProviderSpecDrift> {
  return portalFetch<ProviderSpecDrift>(
    `${SPEC_BASE}/${connectorTypeId}/spec/drift`,
    { method: "GET", token },
  );
}

/**
 * B2C METADATA WRITE (when `dryRun` is false).
 *
 * `expectedSpecVersionId` is REQUIRED for a real apply — the server rejects
 * dry_run=false without it, so "preview before apply" is enforced there, not here —
 * and 409s if the spec moved since the dry run. An apply is NOT atomic: it stops at
 * the first failure and reports what landed; `apply_run_id` is the revert handle.
 */
export async function applyConnectorTypeSpec(
  token: string,
  connectorTypeId: number | string,
  options: {
    dryRun: boolean;
    enable: boolean;
    expectedSpecVersionId: string | null;
    /** dp #2392: destructive deletes this apply may run, by exact record name. */
    confirmDelete?: string[];
    /** dp #2392 D20: destructive property / licence-link removals it may run. */
    confirmRemove?: ProviderSpecConfirmRemove[];
  },
): Promise<ProviderSpecApplyResult> {
  return portalFetch<ProviderSpecApplyResult>(
    `${SPEC_BASE}/${connectorTypeId}/spec/apply`,
    {
      method: "POST",
      token,
      body: {
        dry_run: options.dryRun,
        enable: options.enable,
        expected_spec_version_id: options.expectedSpecVersionId,
        // dp #2392: sent only when non-empty, so a dp revision without the gate
        // (its request model forbids unknown keys) still serves the dry run.
        ...(options.confirmDelete?.length ? { confirm_delete: options.confirmDelete } : {}),
        ...(options.confirmRemove?.length ? { confirm_remove: options.confirmRemove } : {}),
      },
    },
  );
}

export type ProviderSpecCatchUpRequestResult = {
  /** The revert handle: reverting it deletes these marks. */
  apply_run_id: string;
  connector_type_id: number;
  records: string[];
  requested_at_utc: string;
};

export type ProviderSpecCatchUpConnectorState = {
  client_connector_id: number;
  connector_key: string | null;
  name: string | null;
  state: "done" | "in_progress" | "pending" | "unknown";
  started_at_utc: string | null;
  completed_at_utc: string | null;
  note: string | null;
  error: string | null;
};

export type ProviderSpecCatchUpState = {
  type: string;
  connector_type_id: number;
  /** The hand-applied marks table script has not run: no marks can exist yet. */
  marks_table_missing: boolean;
  records: Array<{
    record: string;
    record_id: number;
    mark_id: number;
    requested_at_utc: string;
    apply_run_id: string | null;
    requested_by: string | null;
    reason: string | null;
    counts: { done: number; in_progress: number; pending: number; unknown: number };
    connectors: ProviderSpecCatchUpConnectorState[];
  }>;
  connectors_checked: number;
};

/**
 * B2C METADATA WRITE (catalogue marks only): every ACTIVE connector of the type
 * re-pulls each named ROOT record's whole history once, at its next run. Revertable
 * through `revertApplyRun(result.apply_run_id)`.
 */
export async function requestProviderSpecCatchUp(
  token: string,
  connectorTypeId: number | string,
  records: string[],
  reason: string,
): Promise<ProviderSpecCatchUpRequestResult> {
  return portalFetch<ProviderSpecCatchUpRequestResult>(
    `${SPEC_BASE}/${connectorTypeId}/spec/catch-up`,
    { method: "POST", token, body: { records, reason } },
  );
}

/** Read-only: the type's latest catch-up mark per root record and each connector's progress. */
export async function fetchProviderSpecCatchUp(
  token: string,
  connectorTypeId: number | string,
): Promise<ProviderSpecCatchUpState> {
  return portalFetch<ProviderSpecCatchUpState>(
    `${SPEC_BASE}/${connectorTypeId}/spec/catch-up`,
    { method: "GET", token },
  );
}

/** B2C METADATA WRITE: replay one apply run's change sets backwards. */
export async function revertApplyRun(
  token: string,
  applyRunId: string,
): Promise<RevertRunResult> {
  return portalFetch<RevertRunResult>(
    `/api/v1/gateway/business2cloud/api/v1/connectors/admin/apply-runs/${applyRunId}/revert`,
    { method: "POST", token },
  );
}
